import React, { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Table } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { UserDataFragment } from 'types/graphql'

import { EllipsisButton } from 'src/components/EllipsisButton'
import { useGetAIStaffMembersQuery, useUpdateUserIsAIStaffMemberMutation } from 'src/fetch/users'
import { ActionItem } from 'src/govwell-ui/types'
import { useMyOrgId, useRefetchAppState } from 'src/layouts/AppStateContextLayout/utils'
import { formatUserName } from 'src/utils'

const AIAdminTable = () => {
  const getRowId = useCallback((user: UserDataFragment) => `${user.id}`, [])
  const tableProps = useTableState<UserDataFragment>({
    initialState: {
      sorting: [{ id: 'name', desc: false }],
    },
  })
  const { users, isFetching, refetch } = useGetAIStaffMembersQuery()
  const { mutateAsync: updateUserIsAIStaffMember } = useUpdateUserIsAIStaffMemberMutation()
  const refetchAppState = useRefetchAppState()
  const organizationId = useMyOrgId()

  const handleRemoveFromAIAdmin = useCallback(
    async (userId: number) => {
      await updateUserIsAIStaffMember({ id: userId, isAIStaffMember: false, organizationId })
      await refetch()
      await refetchAppState?.()
    },
    [updateUserIsAIStaffMember, organizationId, refetch, refetchAppState]
  )

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<UserDataFragment>()
    return [
      columnHelper.accessor((user) => formatUserName(user), {
        id: 'name',
        header: 'Name',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((user) => user.emailAddress, {
        id: 'email',
        header: 'Email',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      columnHelper.accessor((user) => user.title, {
        id: 'title',
        header: 'Title',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        meta: { textAlign: 'right' },
        cell: (info) => {
          const user = info.row.original
          const removeItem: ActionItem = {
            label: 'Remove from AI Admin',
            key: `${user.id}-remove`,
            icon: 'FaTrash',
            onClick: () => handleRemoveFromAIAdmin(user.id),
            isDestructive: true,
          }
          return <EllipsisButton items={[removeItem]} />
        },
      }),
    ]
  }, [handleRemoveFromAIAdmin])

  return (
    <Table
      columns={columns}
      data={users}
      enablePagination={false}
      getRowId={getRowId}
      isLoading={isFetching}
      isSortedOnClient
      totalCount={users?.length ?? 0}
      emptyStateMessage="AI Admin has no members."
      {...tableProps}
    />
  )
}

export default React.memo(AIAdminTable)
